/**
 * TanStack Query factories for podiatrist + admin dashboards.
 * Uses RPCs defined in 20260604000100_phase4a_rpcs.sql, updated by
 * 20261103000000_dashboard_podiatrist_strict_isolation.sql to accept
 * an optional p_podiatrist_id and to treat a null p_clinic_id as
 * "every clinic the caller belongs to" (instead of "first clinic").
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export interface DashboardKpis {
  open_orders: number;
  in_production: number;
  awaiting_pickup: number;
  delivered_30d: number;
  clinic_id?: string | null;
}

export interface ActivityRow {
  history_id: string;
  order_id: string;
  order_number: string;
  order_line_id: string | null;
  patient_name: string | null;
  from_status: string | null;
  to_status: string;
  changed_at: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_role: string | null;
  reason: string | null;
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  // Podiatrist KPI cache key includes both clinicId and podiatristId so
  // two podiatrists sitting at the same desk (or one user switching
  // between scopes) never reuse each other's cached counts.
  kpisPodiatrist: (
    clinicId: string | null | undefined,
    podiatristId: string | null | undefined,
  ) =>
    [
      ...dashboardKeys.all,
      'kpis',
      'podiatrist',
      clinicId ?? null,
      podiatristId ?? null,
    ] as const,
  kpisAdmin: () => [...dashboardKeys.all, 'kpis', 'admin'] as const,
  // Activity cache key gains podiatristId for the same reason as KPIs.
  activity: (
    clinicId: string | null | undefined,
    limit: number,
    podiatristId: string | null | undefined,
  ) =>
    [
      ...dashboardKeys.all,
      'activity',
      clinicId ?? null,
      limit,
      podiatristId ?? null,
    ] as const,
};

export function podiatristKpisQuery(
  client: SupabaseClient<Database>,
  clinicId: string | null,
  podiatristId: string | null,
) {
  return {
    queryKey: dashboardKeys.kpisPodiatrist(clinicId, podiatristId),
    queryFn: async (): Promise<DashboardKpis> => {
      const args: { p_clinic_id?: string; p_podiatrist_id?: string } = {};
      if (clinicId) args.p_clinic_id = clinicId;
      if (podiatristId) args.p_podiatrist_id = podiatristId;
      const { data, error } = await client.rpc('podiatrist_dashboard_kpis', args);
      if (error) throw error;
      const j = (data ?? {}) as Record<string, unknown>;
      return {
        clinic_id: (j.clinic_id ?? null) as string | null,
        open_orders: Number(j.open_orders ?? 0),
        in_production: Number(j.in_production ?? 0),
        awaiting_pickup: Number(j.awaiting_pickup ?? 0),
        delivered_30d: Number(j.delivered_30d ?? 0),
      };
    },
  };
}

export function adminKpisQuery(client: SupabaseClient<Database>) {
  return {
    queryKey: dashboardKeys.kpisAdmin(),
    queryFn: async (): Promise<DashboardKpis> => {
      const { data, error } = await client.rpc('admin_dashboard_kpis');
      if (error) throw error;
      const j = (data ?? {}) as Record<string, unknown>;
      return {
        open_orders: Number(j.open_orders ?? 0),
        in_production: Number(j.in_production ?? 0),
        awaiting_pickup: Number(j.awaiting_pickup ?? 0),
        delivered_30d: Number(j.delivered_30d ?? 0),
      };
    },
  };
}

export function recentActivityQuery(
  client: SupabaseClient<Database>,
  clinicId: string | null,
  limit = 10,
  podiatristId: string | null = null,
) {
  return {
    queryKey: dashboardKeys.activity(clinicId, limit, podiatristId),
    queryFn: async (): Promise<ActivityRow[]> => {
      const args: { p_clinic_id?: string; p_limit?: number; p_podiatrist_id?: string } = {
        p_limit: limit,
      };
      if (clinicId) args.p_clinic_id = clinicId;
      if (podiatristId) args.p_podiatrist_id = podiatristId;
      const { data, error } = await client.rpc('clinic_recent_activity', args);
      if (error) throw error;
      return (data ?? []) as ActivityRow[];
    },
  };
}
